/**
 * DeviceLook — the phone/tablet's physical orientation as a look input, for
 * the mobile control schemes ("turn the device to look").
 *
 * Reads `deviceorientation` (alpha/beta/gamma), builds the direction the
 * BACK of the device points in the earth frame, and reports it as a compass
 * heading + pitch. Consumers integrate the per-frame CHANGE (`consume()`), so
 * the input composes with a swipe instead of fighting it. Working from the
 * back-camera direction rather than raw beta (the legacy viewer's shortcut)
 * makes it correct in landscape too — an iPad held sideways is the primary
 * test device.
 *
 * Pitch is gravity-referenced, so it is ABSOLUTE: `pitch()` is 0 with the
 * device upright. `takeAlign()` tells a consumer when the first reading has
 * landed, so it can line the view up once (upright = level) — no "face the
 * wall before you start" instruction, whatever pose the scheme tap caught.
 * Heading stays relative: nobody cares which compass direction a room faces.
 *
 * Between the sensor and the consumer sit three cleanups, all tuned by the
 * dials below:
 *  - Flat blind spot. Flat on a table the back points straight down and has
 *    no compass direction — noise swung the old heading by up to 180° a
 *    frame. The screen's top edge takes over as the device tips flat, so the
 *    heading stays defined in every pose (see poseFromEuler).
 *  - Tremor. A One Euro filter: heavy smoothing while the device barely
 *    moves, almost none on a real turn, so holding it doesn't shimmer and
 *    turning it doesn't lag.
 *  - Rest lock. Still for STILL_MS = the view freezes, and slow compass drift
 *    is soaked up while it rests; it unlocks once the device leaves its rest
 *    pose by UNLOCK_RAD (picked up, or turned on a stand), easing out of the
 *    lock instead of snapping.
 *
 * iOS 13+ gates the event behind `DeviceOrientationEvent.requestPermission()`,
 * which must be called from a user gesture — `start()` therefore has to run
 * inside the tap that picks the scheme (it does: applyScheme runs in the
 * tile's click handler). Denied or unsupported = the input is simply silent.
 */

// ── Dials: starting guesses, tuned on the iPad. ──
const DEG = Math.PI / 180;
/** Moving less than this for STILL_MS = the device is resting. */
const STILL_RAD = 0.3 * DEG;
const STILL_MS = 1000;
/** A resting view unlocks once the device leaves its rest pose by this much. */
const UNLOCK_RAD = 3 * DEG;
/** How fast the view catches up with the device after unlocking (ms). */
const UNLOCK_EASE_MS = 120;
/** While resting, compass drift is absorbed over this time constant: slow
 *  enough that picking the device up still unlocks, fast enough that drift
 *  never builds up to UNLOCK_RAD. */
const DRIFT_TAU_MS = 4000;
/** One Euro filter: cutoff at rest (Hz), how much speed opens it up, and the
 *  speed estimate's own cutoff (Hz). Lower MIN_HZ = smoother but laggier. */
const SMOOTH_MIN_HZ = 1.5;
const SMOOTH_BETA = 4;
const SMOOTH_D_HZ = 1;
/** A gap longer than this (tab hidden, events paused) restarts the filters
 *  rather than gliding across it. */
const GAP_MS = 500;

const wrap = (a: number): number => {
    const t = 2 * Math.PI;
    return a - t * Math.floor((a + Math.PI) / t);
};

const lowpassAlpha = (hz: number, dtS: number): number => {
    const tau = 1 / (2 * Math.PI * hz);
    return 1 / (1 + tau / dtS);
};

/** One Euro filter on one angle (unwrapped radians). */
class OneEuro {
    private x = 0;

    private dx = 0;

    reset(x: number): void {
        this.x = x;
        this.dx = 0;
    }

    step(x: number, dtS: number): number {
        const rawDx = (x - this.x) / dtS;
        this.dx += (rawDx - this.dx) * lowpassAlpha(SMOOTH_D_HZ, dtS);
        const hz = SMOOTH_MIN_HZ + SMOOTH_BETA * Math.abs(this.dx);
        this.x += (x - this.x) * lowpassAlpha(hz, dtS);
        return this.x;
    }
}

/** Screen rotation in radians, counter-clockwise device turns positive (the
 *  iOS `window.orientation` convention; 90 = home button on the right). */
const screenAngle = (): number => {
    const legacy = (window as unknown as { orientation?: unknown }).orientation;
    if (typeof legacy === 'number') return legacy * DEG;
    const a = screen.orientation?.angle;
    return typeof a === 'number' ? a * DEG : 0;
};

/**
 * Heading + pitch of the device's back from W3C alpha/beta/gamma (radians).
 * Exported for the maths test only.
 */
export function poseFromEuler(a: number, b: number, g: number, screenRad: number): { heading: number; pitch: number } {
    // W3C: R = Rz(alpha) · Rx(beta) · Ry(gamma), device → earth (X east,
    // Y north, Z up).
    const sg = Math.sin(g), cg = Math.cos(g);
    const sb = Math.sin(b), cb = Math.cos(b);
    const sa = Math.sin(a), ca = Math.cos(a);
    const rotate = (x: number, y: number, z: number): [number, number, number] => {
        const x1 = cg * x + sg * z, z1 = -sg * x + cg * z;    // Ry(gamma)
        const y2 = cb * y - sb * z1, z2 = sb * y + cb * z1;   // Rx(beta)
        return [ca * x1 - sa * y2, sa * x1 + ca * y2, z2];    // Rz(alpha)
    };
    // The back of the device is (0, 0, -1) in device coords; screen rotation
    // spins about device Z and leaves it alone.
    const [bx, by, bz] = rotate(0, 0, -1);
    // The screen's top edge, which DOES depend on how the screen is rotated.
    const sr = Math.sin(screenRad), cr = Math.cos(screenRad);
    const [ux, uy, uz] = rotate(sr, cr, 0);
    // The heading is the horizontal direction lying in the plane of the back
    // and the top edge: back·uz − top·bz has zero height by construction.
    // Upright (bz = 0) it is exactly the back's heading; flat (uz = 0) it is
    // the top edge's; in between it turns smoothly, including tipped past
    // flat. It only vanishes with the page held sideways on its edge, a pose
    // nobody uses. (Blending the two headings instead left a dead cone ~38°
    // past flat where they cancelled.)
    const heading = Math.atan2(bx * uz - ux * bz, by * uz - uy * bz);
    const pitch = Math.asin(Math.max(-1, Math.min(1, bz)));
    return { heading, pitch };
}

export class DeviceLook {
    private listening = false;

    private hasReading = false;

    private alignPending = false;

    private lastT = 0;

    // Raw heading, unwrapped so the filters never see a ±π seam.
    private rawHeadingPrev = 0;

    private rawHeading = 0;

    private readonly headingFilter = new OneEuro();

    private readonly pitchFilter = new OneEuro();

    // Rest lock.
    private resting = false;

    private anchorHeading = 0;

    private anchorPitch = 0;

    private anchorT = 0;

    private restHeading = 0;

    private restPitch = 0;

    /** Compass drift soaked up while resting — subtracted from then on. */
    private driftOffset = 0;

    /** What the view still owes the device after an unlock; eases to 0. */
    private owedHeading = 0;

    private owedPitch = 0;

    // Output, radians. heading = compass-style, clockwise seen from above
    // (unwrapped); pitch = +up, 0 = upright.
    private heading = 0;

    private pitchOut = 0;

    // Where the consumer last read up to.
    private prevHeading = 0;

    private prevPitch = 0;

    private readonly onOrientation = (e: DeviceOrientationEvent) => {
        if (e.alpha === null || e.beta === null || e.gamma === null) return;
        const pose = poseFromEuler(e.alpha * DEG, e.beta * DEG, e.gamma * DEG, screenAngle());
        const t = e.timeStamp || performance.now();
        const dtMs = t - this.lastT;
        this.lastT = t;

        const fresh = !this.hasReading || dtMs <= 0 || dtMs > GAP_MS;
        if (fresh) {
            this.rawHeading = pose.heading;
        } else {
            this.rawHeading += wrap(pose.heading - this.rawHeadingPrev);
        }
        this.rawHeadingPrev = pose.heading;

        if (fresh) {
            this.headingFilter.reset(this.rawHeading);
            this.pitchFilter.reset(pose.pitch);
            this.resting = false;
            this.owedHeading = 0;
            this.owedPitch = 0;
            this.anchorHeading = this.rawHeading - this.driftOffset;
            this.anchorPitch = pose.pitch;
            this.anchorT = t;
            this.heading = this.rawHeading - this.driftOffset;
            this.pitchOut = pose.pitch;
            if (!this.hasReading) {
                this.prevHeading = this.heading;
                this.prevPitch = this.pitchOut;
                this.hasReading = true;
                this.alignPending = true;
            }
            return;
        }

        const dtS = dtMs / 1000;
        let h = this.headingFilter.step(this.rawHeading, dtS) - this.driftOffset;
        const p = this.pitchFilter.step(pose.pitch, dtS);

        if (this.resting) {
            // Soak up slow drift: move the offset a little toward wherever the
            // heading has wandered. A real pickup outruns this by far.
            const soak = (h - this.restHeading) * Math.min(1, dtMs / DRIFT_TAU_MS);
            this.driftOffset += soak;
            h -= soak;
            if (Math.max(Math.abs(h - this.restHeading), Math.abs(p - this.restPitch)) > UNLOCK_RAD) {
                // Leave the rest pose from exactly where the view is, and let
                // the owed difference ease in over UNLOCK_EASE_MS.
                this.resting = false;
                this.owedHeading = h - this.restHeading;
                this.owedPitch = p - this.restPitch;
                this.anchorHeading = h;
                this.anchorPitch = p;
                this.anchorT = t;
            } else {
                return; // frozen at the rest pose
            }
        } else if (Math.max(Math.abs(h - this.anchorHeading), Math.abs(p - this.anchorPitch)) > STILL_RAD) {
            this.anchorHeading = h;
            this.anchorPitch = p;
            this.anchorT = t;
        } else if (t - this.anchorT >= STILL_MS) {
            // Rest where the view already is, so locking never twitches it.
            this.resting = true;
            this.restHeading = this.heading;
            this.restPitch = this.pitchOut;
            this.driftOffset += h - this.heading;
            this.owedHeading = 0;
            this.owedPitch = 0;
            return;
        }

        const decay = Math.exp(-dtMs / UNLOCK_EASE_MS);
        this.owedHeading *= decay;
        this.owedPitch *= decay;
        this.heading = h - this.owedHeading;
        this.pitchOut = p - this.owedPitch;
    };

    /** Ask (iOS) and subscribe. Safe to call twice. */
    start(): void {
        if (this.listening) return;
        this.listening = true;
        const subscribe = () => {
            if (!this.listening) return; // stopped while the prompt was up
            window.addEventListener('deviceorientation', this.onOrientation);
        };
        try {
            const doe = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> };
            if (typeof doe.requestPermission === 'function') {
                doe.requestPermission()
                    .then((state) => { if (state === 'granted') subscribe(); })
                    .catch(() => { /* denied or not from a gesture — silent */ });
            } else {
                subscribe();
            }
        } catch { /* no API at all */ }
    }

    stop(): void {
        if (!this.listening) return;
        this.listening = false;
        window.removeEventListener('deviceorientation', this.onOrientation);
        this.hasReading = false;
        this.alignPending = false;
        this.resting = false;
        this.driftOffset = 0;
    }

    /**
     * Change since the last call, radians. Zero until the first reading
     * lands. `dHeading` positive = the device turned right.
     */
    consume(): { dHeading: number; dPitch: number } {
        if (!this.hasReading) return { dHeading: 0, dPitch: 0 };
        const dh = wrap(this.heading - this.prevHeading);
        const dp = this.pitchOut - this.prevPitch;
        this.prevHeading = this.heading;
        this.prevPitch = this.pitchOut;
        return { dHeading: dh, dPitch: dp };
    }

    /** The device's pitch right now, radians, 0 = upright, + = tilted up.
     *  null before the first reading. */
    pitch(): number | null {
        return this.hasReading ? this.pitchOut : null;
    }

    /** True exactly once after `start()`, when the first reading lands — the
     *  moment for the consumer to line the view up with the device. */
    takeAlign(): boolean {
        if (!this.alignPending) return false;
        this.alignPending = false;
        return true;
    }

    /** Devtools: is the rest lock holding the view? */
    get isResting(): boolean {
        return this.resting;
    }
}
